<script setup lang="ts">
import ChevronDownIcon from '@/assets/icons/ChevronDownIcon.vue';
import DateSelectionPopover from '@/core/selects/DateSelectionPopover.vue';
import { getDateForObservationTimeline } from '@/utils/date.utils';
import { resolveDialogTeleportTarget } from '@/utils/dom.utils';
import { EPosition } from 'common/enum/misc.enum';
import { computed, nextTick, ref, useTemplateRef } from 'vue';

const props = withDefaults(
  defineProps<{
    alwaysShowMobileChevron?: boolean;
    date: string;
    disabled?: boolean;
    loading?: boolean;
    theme?: 'default' | 'note';
  }>(),
  {
    alwaysShowMobileChevron: false,
    disabled: false,
    loading: false,
    theme: 'default',
  },
);

const emit = defineEmits<{
  'update:date': [date: string];
}>();

const containerRef = useTemplateRef<HTMLElement>('containerRef');
const triggerRef = useTemplateRef<HTMLButtonElement>('triggerRef');
const datePopoverRef = useTemplateRef<InstanceType<typeof DateSelectionPopover>>('datePopoverRef');
const open = ref(false);

const label = computed(() => getDateForObservationTimeline(props.date));
const interactionDisabled = computed(() => props.disabled || props.loading);
const teleportTarget = computed(() => resolveDialogTeleportTarget(containerRef.value));

const toggle = async (): Promise<void> => {
  if (interactionDisabled.value) return;
  if (open.value) {
    datePopoverRef.value?.hide();
    return;
  }

  open.value = true;
  await nextTick();
  datePopoverRef.value?.show();
};

const hide = (): void => {
  datePopoverRef.value?.hide();
  open.value = false;
};

defineExpose({ hide });
</script>

<template>
  <span ref="containerRef" class="inline-flex min-w-0 max-w-full">
    <DateSelectionPopover
      ref="datePopoverRef"
      :anchor-element="triggerRef"
      :date="date"
      :date-picker-position="EPosition.BOTTOM_RIGHT"
      :date-picker-teleport="teleportTarget"
      @hide="open = false"
      @update:date="emit('update:date', $event)"
    />

    <button
      ref="triggerRef"
      type="button"
      class="flex max-w-full items-center gap-xs rounded-sm text-body font-normal leading-[1.7] outline-inner transition-colors hover:text-primary active:text-primary disabled:cursor-default max-md:text-footer max-md:leading-[1.6] max-md:tracking-[0.01em]"
      :class="[
        theme === 'note' ? 'text-primary disabled:text-primary' : 'text-secondary disabled:text-secondary',
        !interactionDisabled && 'group',
      ]"
      :disabled="interactionDisabled"
      aria-haspopup="dialog"
      :aria-expanded="open"
      :aria-label="`${$t('date.select')}: ${label}`"
      :aria-busy="loading || undefined"
      @click="toggle"
    >
      <span class="truncate">{{ label }}</span>
      <ChevronDownIcon
        v-if="!interactionDisabled"
        class="size-icon-sm shrink-0 text-secondary"
        :class="[
          open ? 'flex' : 'hidden group-hover:flex group-focus-visible:flex',
          alwaysShowMobileChevron && 'max-md:flex',
        ]"
        aria-hidden="true"
      />
    </button>
  </span>
</template>
